// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import { EquipmentUnitDataConeCrusher } from '../../../../../../types/crusher';

// Components
import { Button, ButtonSize, ButtonType } from '../../../../../../layout/elements/Button';
import { Checkbox } from '../../../../../../layout/elements/Checkbox';
import { Check, Close } from '../../../../../../layout/icons';
import AlertLine from '../../../../../../layout/icons/AlertLine';
import { ModalPanel, ModalSize } from '../../../../../../layout/elements/ModalPanel';
import { Tooltip } from '../../../../../../layout/elements/Tooltip';

// View
import { TagSelectionModalProps } from './TagSelectionModal.types';
import {
  MAX_SELECTED_TAGS,
  MAX_Y_AXES,
  SENSOR_CATEGORY_ORDER,
  SENSOR_CONFIG,
  SERIES_COLOR_PALETTE,
  SensorCategory,
  SensorConfig,
  SensorTag,
} from '../customChartConfig';
import { SeriesChip } from '../../../../../../layout/elements/SeriesChip';
import {
  CategorySection,
  CategoryTitle,
  ChipsContainer,
  DisabledSensorIcon,
  FooterActions,
  LimitMessage,
  MobileCategoryItem,
  MobileCategoryPanel,
  MobileLayout,
  MobileSensorPanel,
  ModalColumn,
  ModalContent,
  ModalFooter,
  SelectionCount,
  SelectionCountContainer,
  SelectionHelpText,
  SensorGrid,
  SensorItem,
  SensorLabel,
  TagSelectionModalContainer,
  TagSelectionModalGlobalStyle,
} from './TagSelectionModal.style';

/* **************************************** CONSTANTS ****************************************** */

const TRANSLATE_PATH: string = 'view.crusher.trendings.custom';
const COLUMN_COUNT: number = 3;
const COLUMN_KEYS: [string, string, string] = [
  'col-left',
  'col-center',
  'col-right',
];

/* ***************************************** HELPERS ******************************************* */

const isSensorAllowedForModel = (
  config: SensorConfig,
  crusherModel: TagSelectionModalProps['crusherModel'],
): boolean => {
  let isAllowed: boolean = true;

  if (config.requiredModel) {
    isAllowed = crusherModel === config.requiredModel;
  }

  return isAllowed;
};

const isSensorEnabledByFlag = (
  config: SensorConfig,
  lastUnitData: EquipmentUnitDataConeCrusher | null,
): boolean => {
  let isEnabled: boolean = true;

  if (config.enableFlag && lastUnitData) {
    const flagValue: boolean | number | null = lastUnitData[
      config.enableFlag as keyof typeof lastUnitData
    ] as boolean | number | null;

    isEnabled = !!flagValue;
  }

  return isEnabled;
};

// Distribute non-empty categories across columns balancing by sensor count (greedy)
const mapCategoriesToColumns = (
  availableSensors: Record<SensorCategory, SensorTag[]>,
): SensorCategory[][] => {
  const activeCategories: SensorCategory[] = SENSOR_CATEGORY_ORDER.filter(
    (cat: SensorCategory) => availableSensors[cat].length > 0,
  );

  const cols: SensorCategory[][] = [[], [], []];
  const colSizes: number[] = [0, 0, 0];

  activeCategories.forEach((cat: SensorCategory) => {
    const minIndex: number = colSizes.indexOf(Math.min(...colSizes));
    cols[minIndex].push(cat);
    colSizes[minIndex] += availableSensors[cat].length;
  });

  return cols.slice(0, COLUMN_COUNT);
};

/* **************************************** COMPONENT ****************************************** */

function TagSelectionModal({
  crusherModel,
  getContainer,
  isAdmin,
  lastUnitData,
  onApply,
  onCancel,
  selectedTags,
  showModal,
  testId,
}: TagSelectionModalProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs, isSm, isMd } = useBreakpoint();

  const isMobileOrSmallTablet: boolean = isXs || isSm || isMd;

  /* **********************************************************************************************
  ****************************************** LOCAL STATE ******************************************
  ********************************************************************************************** */

  const [localSelectedTags, setLocalSelectedTags] = useState<SensorTag[]>(selectedTags);
  const [activeMobileCategory, setActiveMobileCategory] = useState<SensorCategory | null>(null);

  const availableSensors: Record<SensorCategory, SensorTag[]> = useMemo(
    (): Record<SensorCategory, SensorTag[]> => {
      const result: Record<SensorCategory, SensorTag[]> = {
        [SensorCategory.hydraulic]: [],
        [SensorCategory.motor]: [],
        [SensorCategory.lube]: [],
        [SensorCategory.vibration]: [],
        [SensorCategory.cavityLevel]: [],
      };

      const sensorEntries: [SensorTag, SensorConfig][] = Object.entries(
        SENSOR_CONFIG,
      ) as [SensorTag, SensorConfig][];

      sensorEntries.forEach(([tag, config]: [SensorTag, SensorConfig]): void => {
        if (!isSensorAllowedForModel(config, crusherModel)) return;

        if (!isAdmin && !isSensorEnabledByFlag(config, lastUnitData)) return;

        result[config.category].push(tag);
      });

      return result;
    },
    [
      crusherModel,
      isAdmin,
      lastUnitData,
    ],
  );

  const currentUnits: Set<string> = useMemo(
    (): Set<string> => new Set(
      localSelectedTags
        .map((tag: SensorTag) => SENSOR_CONFIG[tag]?.unit)
        .filter((unit: string | undefined): unit is string => unit !== undefined),
    ),
    [localSelectedTags],
  );

  const isMaxSelected: boolean = localSelectedTags.length >= MAX_SELECTED_TAGS;

  const isMaxAxes: boolean = currentUnits.size >= MAX_Y_AXES;

  const showMaxTagsMessage: boolean = isMaxSelected;

  const showMaxAxesMessage: boolean = !isMaxSelected && isMaxAxes;

  const columns: SensorCategory[][] = useMemo(
    (): SensorCategory[][] => mapCategoriesToColumns(availableSensors),
    [availableSensors],
  );

  const mobileCategories: SensorCategory[] = useMemo(
    (): SensorCategory[] => SENSOR_CATEGORY_ORDER.filter(
      (cat: SensorCategory) => availableSensors[cat].length > 0,
    ),
    [availableSensors],
  );

  const displayedCategory: SensorCategory | null = activeMobileCategory
    ?? mobileCategories[0]
    ?? null;

  /* **********************************************************************************************
  ******************************************** METHODS ********************************************
  ********************************************************************************************** */

  const isSensorFlagDisabled = useCallback((tag: SensorTag): boolean => {
    const config: SensorConfig | undefined = SENSOR_CONFIG[tag];

    return !isSensorEnabledByFlag(
      config,
      lastUnitData,
    );
  }, [lastUnitData]);

  const wouldExceedAxisLimit = useCallback((tag: SensorTag): boolean => {
    const tagUnit: string | undefined = SENSOR_CONFIG[tag]?.unit;

    /* Sensors without a declared unit are blocked by default to avoid
     * adding an unmeasured axis to the chart */
    let exceedsLimit: boolean = true;

    if (tagUnit !== undefined) {
      exceedsLimit = !currentUnits.has(tagUnit)
        && currentUnits.size >= MAX_Y_AXES;
    }

    return exceedsLimit;
  }, [currentUnits]);

  const isTagDisabled = useCallback((tag: SensorTag): boolean => {
    let isDisabled: boolean = isMaxSelected || wouldExceedAxisLimit(tag);

    if (localSelectedTags.includes(tag)) {
      isDisabled = false;
    }

    return isDisabled;
  }, [localSelectedTags, isMaxSelected, wouldExceedAxisLimit]);

  const handleToggleTag = useCallback((tag: SensorTag): void => {
    const isSelected: boolean = localSelectedTags.includes(tag);

    let updatedTags: SensorTag[];

    if (isSelected) {
      updatedTags = localSelectedTags.filter(
        (selectedTag: SensorTag) => selectedTag !== tag,
      );
    } else if (!isMaxSelected && !wouldExceedAxisLimit(tag)) {
      updatedTags = [...localSelectedTags, tag];
    } else {
      updatedTags = localSelectedTags;
    }

    setLocalSelectedTags(updatedTags);
  }, [localSelectedTags, isMaxSelected, wouldExceedAxisLimit]);

  const handleApply = useCallback((): void => {
    onApply(localSelectedTags);
  }, [localSelectedTags, onApply]);

  const renderSensorRow = useCallback((tag: SensorTag): JSX.Element => {
    const config: SensorConfig = SENSOR_CONFIG[tag];
    const isChecked: boolean = localSelectedTags.includes(tag);
    const isDisabled: boolean = isTagDisabled(tag);
    const selectionIndex: number = localSelectedTags.indexOf(tag);
    const tagColor: string = selectionIndex >= 0
      ? SERIES_COLOR_PALETTE[selectionIndex]
      : SERIES_COLOR_PALETTE[0];

    const isFlagDisabled: boolean = isSensorFlagDisabled(tag);
    const sensorLabel: string = config.unit
      ? `${String(t(config.nameKey))} (${config.unit})`
      : String(t(config.nameKey));

    return (
      <SensorItem
        key={tag}
        $isChecked={isChecked}
        $isDisabled={isDisabled}
        $sensorColor={tagColor}
        onClick={
          isDisabled
            ? undefined
            : () => handleToggleTag(tag)
        }
      >
        <Checkbox
          checked={isChecked}
          disabled={isDisabled}
          onChange={() => handleToggleTag(tag)}
          testId={`sensor-checkbox-${tag}`}
        />

        {isFlagDisabled && isAdmin && (
          <Tooltip
            text={t(`${TRANSLATE_PATH}.sensorDisabledTooltip`)}
          >
            <DisabledSensorIcon data-testid="disabled-sensor-icon">
              <AlertLine />
            </DisabledSensorIcon>
          </Tooltip>
        )}

        <SensorLabel $isDisabled={isFlagDisabled}>
          {sensorLabel}
        </SensorLabel>
      </SensorItem>
    );
  }, [
    handleToggleTag,
    isAdmin,
    isSensorFlagDisabled,
    isTagDisabled,
    localSelectedTags,
    t,
  ]);

  /* **********************************************************************************************
  ************************************** COMPONENT HANDLING ***************************************
  ********************************************************************************************** */

  useEffect((): void => {
    if (showModal) {
      setLocalSelectedTags(selectedTags);
      setActiveMobileCategory(null);
    }
  }, [showModal, selectedTags]);

  return (
    <>
      <TagSelectionModalGlobalStyle />

      <ModalPanel
        className="tag-selection-modal"
        getContainer={getContainer}
        onClose={onCancel}
        showModal={showModal}
        size={ModalSize.lg}
        testId={testId}
        title={`${t(`${TRANSLATE_PATH}.configureTitle`)}`}
      >
        <TagSelectionModalContainer>
          {isMobileOrSmallTablet ? (
            <MobileLayout>
              <MobileCategoryPanel data-testid="mobile-category-panel">
                {mobileCategories.map((category: SensorCategory) => (
                  <MobileCategoryItem
                    key={category}
                    $isActive={category === displayedCategory}
                    data-testid={`mobile-category-item-${category}`}
                    onClick={() => setActiveMobileCategory(category)}
                  >
                    {t(`${TRANSLATE_PATH}.categories.${category}`)}
                  </MobileCategoryItem>
                ))}
              </MobileCategoryPanel>

              <MobileSensorPanel data-testid="mobile-sensor-panel">
                {displayedCategory !== null && (
                  <SensorGrid>
                    {availableSensors[displayedCategory].map(renderSensorRow)}
                  </SensorGrid>
                )}
              </MobileSensorPanel>
            </MobileLayout>
          ) : (
            <ModalContent data-testid="modal-content-desktop">
              {columns.map((
                columnCategories: SensorCategory[],
                columnIndex: number,
              ) => (
                <ModalColumn key={COLUMN_KEYS[columnIndex]}>
                  {columnCategories.map((category: SensorCategory) => {
                    const sensors: SensorTag[] = availableSensors[category];

                    return (
                      <CategorySection key={category}>
                        <CategoryTitle>
                          {t(`${TRANSLATE_PATH}.categories.${category}`)}
                        </CategoryTitle>

                        <SensorGrid>
                          {sensors.map(renderSensorRow)}
                        </SensorGrid>
                      </CategorySection>
                    );
                  })}
                </ModalColumn>
              ))}
            </ModalContent>
          )}

          {(showMaxTagsMessage || showMaxAxesMessage) && (
            <LimitMessage
              data-testid={
                showMaxTagsMessage
                  ? 'max-tags-message'
                  : 'max-axes-message'
              }
            >
              {showMaxTagsMessage
                ? t(`${TRANSLATE_PATH}.maxTagsReached`, { max: MAX_SELECTED_TAGS })
                : t(`${TRANSLATE_PATH}.maxAxesReached`)}
            </LimitMessage>
          )}

          <ModalFooter>
            <SelectionCountContainer>
              <SelectionCount data-testid="selection-count">
                {`${t(`${TRANSLATE_PATH}.selected`)} (${localSelectedTags.length}/${MAX_SELECTED_TAGS})`}
              </SelectionCount>

              <SelectionHelpText>
                {t(`${TRANSLATE_PATH}.selectedHelpText`, { max: MAX_SELECTED_TAGS })}
              </SelectionHelpText>
            </SelectionCountContainer>

            {localSelectedTags.length > 0 && (
              <ChipsContainer data-testid="selected-chips">
                {localSelectedTags.map((tag: SensorTag, tagIndex: number): JSX.Element => {
                  const config: SensorConfig = SENSOR_CONFIG[tag];

                  return (
                    <SeriesChip
                      key={tag}
                      color={SERIES_COLOR_PALETTE[tagIndex]}
                      dotIcon={<Check />}
                      label={t(config.nameKey)}
                      onRemove={(): void => handleToggleTag(tag)}
                      removeAriaLabel={String(t('common.remove'))}
                      removeIcon={<Close />}
                      removeTestId={`modal-chip-remove-${tag}`}
                      testId={`modal-chip-${tag}`}
                    />
                  );
                })}
              </ChipsContainer>
            )}

            <FooterActions>
              <Button
                block={false}
                onClick={handleApply}
                size={ButtonSize.xs}
                type={ButtonType.primary}
                testId="tag-modal-apply"
              >
                {t(`${TRANSLATE_PATH}.apply`)}
              </Button>
            </FooterActions>
          </ModalFooter>
        </TagSelectionModalContainer>
      </ModalPanel>
    </>
  );
}

export { TagSelectionModal };
